import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  User,
  Lock,
  CreditCard,
  ExternalLink,
  Coins,
  Sparkles,
} from "lucide-react";
import { useOpenCustomerPortal } from "@/hooks/api/mutate/useOpenCustomerPortal";
import ReferralSection from "@/components/referral/ReferralSection";
import { CreditHistory } from "@/components/profile/CreditHistory";
import { PurchaseHistory } from "@/components/profile/PurchaseHistory";
import { PasswordResetForm } from "@/components/auth/PasswordResetForm";
import { PaywallDialog } from "@/components/PaywallDialog/PaywallDialog";
import { useGetCreditsBalance } from "@/hooks/api/query/useGetCreditsBalance";
import { queryKeys } from "@/lib/queryClient";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const profileSchema = z.object({
  firstName: z.string().min(1, "Numele este obligatoriu"),
  lastName: z.string().min(1, "Prenumele este obligatoriu"),
});

type ProfileForm = z.infer<typeof profileSchema>;

export default function Profile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const customerPortal = useOpenCustomerPortal();
  const { data: creditsData, isLoading: isLoadingCredits } =
    useGetCreditsBalance({
      enabled: !!user,
      queryKey: queryKeys.credits.balance(),
    });
  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
    },
  });

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        throw error;
      }

      if (data) {
        profileForm.setValue("firstName", data.first_name || "");
        profileForm.setValue("lastName", data.last_name || "");
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast({
        title: "Eroare",
        description: "Nu s-a putut încărca profilul",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const onProfileSubmit = async (data: ProfileForm) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          first_name: data.firstName,
          last_name: data.lastName,
        })
        .eq("user_id", user.id);

      if (error) throw error;

      toast({
        title: "Succes",
        description: "Profilul a fost actualizat cu succes",
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Eroare",
        description: "Nu s-a putut actualiza profilul",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto p-6 space-y-8">
      <div className="space-y-2">
        <h1
          data-testid="profile-page-title"
          className="text-3xl font-heading font-bold"
        >
          Profil
        </h1>
        <p className="text-muted-foreground">
          Gestionează informațiile tale personale și securitatea contului
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Profile Information */}
        <Card>
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5" />
              <CardTitle>Informații personale</CardTitle>
            </div>
            <CardDescription>
              Actualizează numele și prenumele tău
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...profileForm}>
              <form
                onSubmit={profileForm.handleSubmit(onProfileSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={profileForm.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nume</FormLabel>
                      <FormControl>
                        <Input placeholder="Introdu numele tău" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={profileForm.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prenume</FormLabel>
                      <FormControl>
                        <Input placeholder="Introdu prenumele tău" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={profileForm.formState.isSubmitting}
                >
                  {profileForm.formState.isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Salvează modificările
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Password Change */}
        <Card>
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              <CardTitle>Schimbă parola</CardTitle>
            </div>
            <CardDescription>
              Actualizează parola pentru a-ți menține contul în siguranță
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PasswordResetForm />
          </CardContent>
        </Card>
      </div>

      <Separator />

      <div className="grid gap-6 md:grid-cols-2">
        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle>Informații cont</CardTitle>
            <CardDescription>Detalii despre contul tău</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label>Email</Label>
              <div className="text-sm text-muted-foreground">{user?.email}</div>
            </div>

            <div className="grid gap-2">
              <Label>ID utilizator</Label>
              <div
                data-testid="profile-user-id"
                className="text-sm text-muted-foreground font-mono"
              >
                {user?.id}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Details */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              <CardTitle>Detalii plată</CardTitle>
            </div>
            <CardDescription>
              Gestionează metoda de plată și datele de facturare
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              disabled={customerPortal.isPending}
              onClick={() => customerPortal.mutate()}
            >
              {customerPortal.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="mr-2 h-4 w-4" />
              )}
              Deschide panoul de plată
            </Button>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* Buy Credits CTA */}
      <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <CardContent className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-primary/10 shrink-0">
              <Coins className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-heading font-bold">
                  Cumpără credite
                </h3>
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground max-w-md">
                Reîncarcă-ți balanța oricând. Creditele nu expiră și pot fi
                folosite pentru orice acțiune din platformă.
              </p>
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-muted-foreground">
                  Balanță curentă:
                </span>
                {isLoadingCredits ? (
                  <span className="text-xs font-bold">...</span>
                ) : (
                  <span className="text-sm font-bold text-primary">
                    {creditsData?.credits?.toLocaleString() ?? 0} credite
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button
            size="lg"
            onClick={() => setBuyDialogOpen(true)}
            className="shrink-0"
          >
            <Coins className="mr-2 h-4 w-4" />
            Cumpără credite
          </Button>
        </CardContent>
      </Card>

      <Separator />

      {/* Past Purchases */}
      <PurchaseHistory />

      <Separator />

      {/* Credit Consumption History */}
      <CreditHistory />

      <Separator />

      {/* Referral System */}
      <ReferralSection />

      <PaywallDialog
        open={buyDialogOpen}
        onOpenChange={setBuyDialogOpen}
        mode="buy"
      />
    </div>
  );
}
